'use client'

import { useChat } from '@ai-sdk/react'
import { useMemo, useState } from 'react'
import { useDispatch, useStore } from 'react-redux'

import type { Quota } from '~/data/api/client/assistant'
import { type AssistantUIMessage, invalidateConversationList } from '~/data/api/client/assistant/enhancedEndpoints'
import { selectViewedEntities } from '~/features/viewedEntities'
import type { RootState } from '~/store'

import { readQuota } from './stream/dataParts'
import { createAssistantTransport } from './stream/transport'

type UseAssistantChatArgs = {
  conversationId: string
  organizationId: string
  initialMessages: AssistantUIMessage[]
  initialQuota?: Quota
}

/** Text updates arrive per token; batching them keeps long answers from re-rendering the thread per chunk. */
const RENDER_THROTTLE_MS = 50

/**
 * `useChat` bound to one assistant-api conversation, plus the quota the latest turn reported. A finished turn
 * refreshes the conversation history. The chat instance
 * is created once per conversation id, so a caller that switches conversation or organization remounts the
 * consumer rather than changing the arguments.
 */
export default function useAssistantChat({
  conversationId,
  organizationId,
  initialMessages,
  initialQuota
}: UseAssistantChatArgs) {
  const dispatch = useDispatch()
  // The panel renders in the layout, outside the page's tree, so the page's entities are read from the store
  // when a turn is sent.
  const store = useStore<RootState>()
  const [quota, setQuota] = useState(initialQuota)
  const transport = useMemo(
    () =>
      createAssistantTransport<AssistantUIMessage>({
        conversationId,
        organizationId,
        getViewedEntities: () => selectViewedEntities(store.getState())
      }),
    [conversationId, organizationId, store]
  )
  const chat = useChat<AssistantUIMessage>({
    id: conversationId,
    messages: initialMessages,
    transport,
    experimental_throttle: RENDER_THROTTLE_MS,
    onData: (part) => {
      const reported = readQuota(part)
      if (reported) setQuota(reported)
    },
    // A turn moves the conversation to the top of the history and may give it a title.
    onFinish: () => {
      dispatch(invalidateConversationList())
    }
  })
  return { ...chat, quota }
}
